import React, {useCallback, useState} from 'react';
import {Trans, useTranslation} from 'next-i18next';
import {
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	Text,
} from '@tempo/core';
import {useToast} from '@deezer/next/shared/hooks/useToast';
import type {ArtistFormData, FormFieldChange} from '../../types';
import type {ArtistSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import ArtistSearchInput from '../../components/ArtistSearchInput';
import UPCInfoTooltip from '../../components/UPCInfoTooltip';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {ApiErrorCode} from '@constants/errorCodes';
import {isErrorCode} from '@deezer/next/services/api/utils';
import {useClaims} from '@deezer/next/contexts/main/hooks';
import {StakeholderType} from '@deezer/next/services/api/Starmania/requests/album/types';
import {ArrowRightIcon} from '@tempo/icons';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FooterRow from '@components/PageElements/FooterRow';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';

type NewArtistFormCardProps = {
	formData: ArtistFormData;
	onFormFieldChange: FormFieldChange<ArtistFormData>;
	onContinue: () => void;
	onBack: () => void;
};

const NewArtistFormCard = ({
	formData,
	onFormFieldChange,
	onContinue,
	onBack,
}: NewArtistFormCardProps) => {
	const {t} = useTranslation('d4c');
	const claims = useClaims();
	const [upcError, setUPCError] = useState<string>();
	const [selectedArtist, setSelectedArtist] = useState<
		Omit<ArtistSearchResult, 'redirectUrl'> | undefined
	>(formData.artist);
	const toast = useToast();
	const handleArtistUPCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUPCError(undefined);
		onFormFieldChange('artistUpc', e.target.value);
	};

	const handleSelectedArtistChange = useCallback(
		(item?: ArtistSearchResult) => {
			onFormFieldChange('artistUpc', '');
			onFormFieldChange('artist', item);
			onFormFieldChange('displayName', item?.name || '');
			setUPCError(undefined);
			setSelectedArtist(item);
		},
		[onFormFieldChange],
	);

	const handleContinue = async () => {
		if (!selectedArtist) return;

		try {
			await StarmaniaApi.album.verifyAlbumUpc(
				formData.artistUpc,
				selectedArtist.id,
				StakeholderType.ARTIST,
			);
			onContinue();
		} catch (e: any) {
			const errorCode = e.response.data.errors?.[0]?.code;

			if (
				isErrorCode(errorCode, [
					ApiErrorCode.INVALID_UPC,
					ApiErrorCode.NOT_FOUND_UPC,
				])
			) {
				setUPCError(
					t('toastmessage_text_upcinvalidordoesntbelongtoXname_d4c-web', {
						sprintf: [formData.artist?.name],
					}),
				);
			} else {
				toast({
					description:
						e.response?.data?.errors?.[0]?.details ||
						t('toastmessage_text_unknownerroroccurred_d4c-web'),
					status: 'error',
				});
			}
		}
	};

	return (
		<PageLayout
			data-testid="new-artist-card"
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							{claims.length > 0 && (
								<Button
									data-testid="back-button"
									variant="outline"
									onClick={onBack}
									size="small"
									leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // TODO fix that
								>
									{t('generic_action_back_d4c-web')}
								</Button>
							)}
							<Button
								data-testid="continue-button"
								isDisabled={
									!formData.artist?.name ||
									!formData.artistUpc ||
									Boolean(upcError)
								}
								size="small"
								onClick={handleContinue}
							>
								{t('generic_action_continue_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<FormSectionTitle
						title={t('analyticsartist_title_yourartist_d4c-web')}
						subtitle={t(
							'requestaccessreviewdisco_text_problemwithdisco_d4c-web',
						)}
						subLink="https://creatorsupport.deezer.com"
					/>
					<Text>
						<Trans
							components={{
								strong: <strong />,
							}}
						>
							{t(
								'requestaccessartist_text_toclaimartistprovidelivereleaseupc_d4c-web',
							)}
						</Trans>
					</Text>
					<FormSectionContainer>
						<FormControl isRequired={true}>
							<FormLabel htmlFor="artist-name-search">
								{/*fixme: translate*/}
								Artist name / artist id
							</FormLabel>
							<ArtistSearchInput
								data-testid="claim-new-artist-search"
								id="artist-name-search"
								// fixme:translate
								placeholder="Enter artist name/id"
								selectedArtist={selectedArtist}
								onSelectedArtistChange={handleSelectedArtistChange}
								displayArtistId={true}
							/>
						</FormControl>
						<FormControl
							isRequired={true}
							isDisabled={!formData.artist?.name}
							isInvalid={Boolean(upcError)}
						>
							<FormLabel htmlFor="upc">
								{t('deliverymonitoring_text_upc_d4c-web')}
								<UPCInfoTooltip top="-1px" ms={4} position="absolute" />
							</FormLabel>
							<Input
								data-testid="claim-new-artist-upc"
								id="upc"
								type="text"
								value={formData.artistUpc}
								onChange={handleArtistUPCChange}
							/>
							<FormErrorMessage>{upcError}</FormErrorMessage>
						</FormControl>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default NewArtistFormCard;
